import React, { useState } from "react";
import {
  AttachmentPreviewList,
  AudioRecorder,
  LinkPreviewList,
  QuotedMessagePreview,
  SimpleAttachmentSelector,
  TextareaComposer,
  useChatContext,
  useMessageComposer,
  useMessageInputContext,
} from "stream-chat-react";
import { Button } from "@/components/ui/button";
import {
  CalendarCheck,
  Loader2,
  Mic,
  MessageSquareQuote,
  NotebookPen,
  PlusCircle,
  SendHorizontal,
  Smile,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { FrontDeskUserSelect } from "./FrontDeskUserSelect";
import { ResolveChatButton } from "./ResolveChatButton";
import { AIChatSettingsSelect } from "./AIChatSettingsSelect";
import { VariableSelector } from "@/components/pages/faqs/VariableSelector";
import { CannedResponseSelector } from "./CannedResponseSelector";
import { useTranslation } from "react-i18next";
import CreateRequestModal from "@/components/pages/requests/components/modals/CreateRequestModal";
import { RequestType } from "@/types/request";
import { useReservation } from "@/hooks/useReservations";
import { CheckoutStatus, Reservation } from "@/types/reservation";
import { SuggestButton } from "./SuggestButton";
import { AddNoteModal } from "./AddNoteModal";
import { useCreateChatNote } from "@/hooks/useChat.hooks";
import { TranslationSettingsSelect } from "./TranslationSettingsSelect";
import { FormatMessageModal } from "./FormatMessageModal";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useHotel } from "@/hooks/useHotel";
import { useMultiChannelComposer } from "@/components/pages/chats/hooks/use-multi-channel-composer.hooks";
import { ReengagementDialog } from "@/components/common/whatsapp/ReengagementDialog";
import {
  MultiChannelIndicatorSlot,
  MultiChannelPickerSlot,
} from "./MultiChannelComposerSlot";
import { useSendPreCheckoutReminder } from "@/hooks/useBookingCheckout";
import { toast } from "sonner";
import { enableAllMentionAutocomplete } from "@/components/pages/chats/utils/channel-mentions";
import {
  CHAT_COMPOSER_INSERT_TEXT_EVENT,
  type ChatComposerInsertTextEventDetail,
} from "@/components/pages/chats/utils/composer-events";

interface CustomMessageInputProps {
  hideRequestButtons?: boolean;
  hideResolvePanel?: boolean;
  hideVariableSelectors?: boolean;
  hideAiSettingsPanel?: boolean;
  hideSuggestButton?: boolean;
  variant?: "default" | "internal";
}

interface CanShowMultiChannelComposerArgs {
  isInternalVariant: boolean;
  channelType: string | undefined;
  bookingId: string | undefined;
  featureEnabled: boolean;
}

const canShowMultiChannelComposer = ({
  isInternalVariant,
  channelType,
  bookingId,
  featureEnabled,
}: CanShowMultiChannelComposerArgs): boolean => {
  if (isInternalVariant) return false;
  if (channelType !== "messaging") return false;
  if (!bookingId) return false;
  return featureEnabled;
};

interface RequestActionsBarProps {
  bookingId?: string;
  roomId?: string;
  shouldShowGuestRequestButton: boolean;
  shouldShowInternalRequestButton: boolean;
  shouldShowPreCheckoutReminderButton: boolean;
  shouldShowNoteButton: boolean;
  guestRequestLabel: string;
  internalRequestLabel: string;
  preCheckoutReminderLabel: string;
  sendingPreCheckoutReminderLabel: string;
  addNoteLabel: string;
  onRequestSuccess: (requestId: string) => Promise<void>;
  onSendPreCheckoutReminder: () => void;
  onAddNote: () => void;
  isSendingPreCheckoutReminder: boolean;
  isAddNoteDisabled: boolean;
}

interface CannedResponsesToolbarButtonProps {
  className: string;
  isVisible: boolean;
  label: string;
  onOpen: () => void;
}

interface MessageInputVisibilityParams {
  canUseRequests: boolean;
  canUseAiAssistant: boolean;
  hideRequestButtons: boolean;
  hideVariableSelectors: boolean;
  hideAiSettingsPanel: boolean;
  hideSuggestButton: boolean;
  isInternalVariant: boolean;
  hasBooking: boolean;
  checkoutStatus?: CheckoutStatus;
}

interface MessageInputVisibility {
  shouldShowGuestRequestButton: boolean;
  shouldShowInternalRequestButton: boolean;
  shouldShowPreCheckoutReminderButton: boolean;
  shouldShowNoteButton: boolean;
  shouldShowActionBar: boolean;
  shouldShowFormatButton: boolean;
  shouldShowVariableButton: boolean;
  shouldShowTranslationSettings: boolean;
  shouldShowAiSettings: boolean;
  shouldShowSuggestButton: boolean;
}

const getMessageInputVisibility = ({
  canUseRequests,
  canUseAiAssistant,
  hideRequestButtons,
  hideVariableSelectors,
  hideAiSettingsPanel,
  hideSuggestButton,
  isInternalVariant,
  hasBooking,
  checkoutStatus,
}: MessageInputVisibilityParams): MessageInputVisibility => {
  const shouldShowRequestButtons = canUseRequests && !hideRequestButtons;
  const shouldShowInternalRequestButton =
    canUseRequests && (isInternalVariant || !hideRequestButtons);
  const shouldShowGuestRequestButton =
    shouldShowRequestButtons && !isInternalVariant;
  const shouldShowNoteButton = !hideRequestButtons && !isInternalVariant;
  const shouldShowPreCheckoutReminderButton =
    !hideRequestButtons &&
    !isInternalVariant &&
    hasBooking &&
    checkoutStatus !== CheckoutStatus.DONE;
  const shouldShowActionBar =
    shouldShowInternalRequestButton ||
    shouldShowPreCheckoutReminderButton ||
    shouldShowNoteButton;
  const shouldShowFormatButton = !hideVariableSelectors || isInternalVariant;
  const shouldShowVariableButton = !hideVariableSelectors && !isInternalVariant;
  const shouldShowTranslationSettings =
    !hideAiSettingsPanel || isInternalVariant;
  const shouldShowAiSettings =
    canUseAiAssistant && !hideAiSettingsPanel && !isInternalVariant;
  const shouldShowSuggestButton = canUseAiAssistant && !hideSuggestButton;

  return {
    shouldShowGuestRequestButton,
    shouldShowInternalRequestButton,
    shouldShowPreCheckoutReminderButton,
    shouldShowNoteButton,
    shouldShowActionBar,
    shouldShowFormatButton,
    shouldShowVariableButton,
    shouldShowTranslationSettings,
    shouldShowAiSettings,
    shouldShowSuggestButton,
  };
};

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object" || !("statusCode" in error)) {
    return undefined;
  }

  return typeof error.statusCode === "number" ? error.statusCode : undefined;
};

const getPreCheckoutReminderErrorKey = (error: unknown): string => {
  const statusCode = getErrorStatusCode(error);

  if (statusCode === 400) {
    return "chats.preCheckoutReminderCheckoutDoneError";
  }

  if (statusCode === 403) {
    return "chats.preCheckoutReminderForbiddenError";
  }

  if (statusCode === 404) {
    return "chats.preCheckoutReminderBookingNotFoundError";
  }

  return "chats.preCheckoutReminderError";
};

const RequestActionsBar: React.FC<RequestActionsBarProps> = ({
  bookingId,
  roomId,
  shouldShowGuestRequestButton,
  shouldShowInternalRequestButton,
  shouldShowPreCheckoutReminderButton,
  shouldShowNoteButton,
  guestRequestLabel,
  internalRequestLabel,
  preCheckoutReminderLabel,
  sendingPreCheckoutReminderLabel,
  addNoteLabel,
  onRequestSuccess,
  onSendPreCheckoutReminder,
  onAddNote,
  isSendingPreCheckoutReminder,
  isAddNoteDisabled,
}) => (
  <div className="mb-4 flex items-center justify-between gap-2">
    <div />

    <div className="flex items-center gap-2">
      {shouldShowGuestRequestButton ? (
        <CreateRequestModal
          requestType={RequestType.GUEST}
          bookingId={bookingId}
          roomId={roomId}
          onSuccess={onRequestSuccess}
          triggerNode={
            <Button
              className="h-9 gap-2 rounded-lg border-slate-200 bg-[#eef5ff] px-4 text-slate-800 transition-all hover:bg-[#e7f0ff] active:scale-95"
              variant="new_outline"
            >
              <PlusCircle className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium">{guestRequestLabel}</span>
            </Button>
          }
        />
      ) : null}

      {shouldShowInternalRequestButton ? (
        <CreateRequestModal
          requestType={RequestType.INTERNAL}
          bookingId={bookingId}
          roomId={roomId}
          triggerNode={
            <Button
              className="h-11 gap-2 rounded-[14px] border-slate-200 bg-[#fff9df] px-4 text-slate-800 transition-all hover:bg-[#fff4c9] active:scale-95"
              variant="new_outline"
            >
              <PlusCircle className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium">
                {internalRequestLabel}
              </span>
            </Button>
          }
        />
      ) : null}

      {shouldShowPreCheckoutReminderButton ? (
        <Button
          type="button"
          className="h-9 gap-2 rounded-lg px-4 transition-all active:scale-95"
          variant="outline"
          onClick={onSendPreCheckoutReminder}
          disabled={isSendingPreCheckoutReminder}
        >
          {isSendingPreCheckoutReminder ? (
            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          ) : (
            <CalendarCheck className="text-muted-foreground h-4 w-4" />
          )}
          <span className="text-sm font-medium">
            {isSendingPreCheckoutReminder
              ? sendingPreCheckoutReminderLabel
              : preCheckoutReminderLabel}
          </span>
        </Button>
      ) : null}
    </div>

    {shouldShowNoteButton ? (
      <Button
        className="h-9 gap-2 rounded-lg border-slate-200 bg-[#fff3e8] px-4 text-slate-800 transition-all hover:bg-[#ffe9d6] active:scale-95"
        variant="new_outline"
        onClick={onAddNote}
        disabled={isAddNoteDisabled}
      >
        <NotebookPen className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium">{addNoteLabel}</span>
      </Button>
    ) : null}
  </div>
);

const CannedResponsesToolbarButton: React.FC<
  CannedResponsesToolbarButtonProps
> = ({ className, isVisible, label, onOpen }) => {
  if (!isVisible) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(className, "border-slate-200")}
      onClick={onOpen}
    >
      <MessageSquareQuote className="h-5 w-5" />
    </button>
  );
};

const CustomMessageInput = ({
  hideRequestButtons = false,
  hideResolvePanel = false,
  hideVariableSelectors = false,
  hideAiSettingsPanel = false,
  hideSuggestButton = false,
  variant = "default",
}: CustomMessageInputProps) => {
  const { t } = useTranslation();
  const [isCannedResponseOpen, setIsCannedResponseOpen] = useState(false);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [isFormatMessageOpen, setIsFormatMessageOpen] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const { handleSubmit, textareaRef, recordingController } =
    useMessageInputContext();
  const { capabilities } = useAccessCapabilities();
  const messageComposer = useMessageComposer();
  const { textComposer } = messageComposer;
  const { channel } = useChatContext();
  const { mutateAsync: createChatNote, isPending: isCreatingNote } =
    useCreateChatNote(channel?.id);
  const {
    mutateAsync: sendPreCheckoutReminder,
    isPending: isSendingPreCheckoutReminder,
  } = useSendPreCheckoutReminder();

  const channelData = channel?.data;
  const bookingId = channelData?.bookingId || channelData?.metadata?.bookingId;

  const { data: channelRelatedBooking } =
    useReservation<Reservation>(bookingId);

  const roomId = channelRelatedBooking?.room?.id;

  const isInternalVariant = variant === "internal";

  React.useEffect(() => {
    if (!isInternalVariant || !channel) return;

    return enableAllMentionAutocomplete(textComposer, channel);
  }, [channel, isInternalVariant, textComposer]);

  const showMultiChannelComposer = canShowMultiChannelComposer({
    isInternalVariant,
    channelType: channel?.data?.type,
    bookingId,
    featureEnabled: capabilities.canUseGuestCommunication,
  });
  const { hotel: currentHotel } = useHotel(showMultiChannelComposer);
  const whatsappTemplateContext = React.useMemo(
    () => ({
      booking: channelRelatedBooking ?? null,
      hotel: currentHotel
        ? { id: currentHotel.id, name: currentHotel.name }
        : null,
    }),
    [channelRelatedBooking, currentHotel],
  );
  const {
    configs: channelConfigs,
    selectedHint,
    setSelectedHint,
    unavailableChannels,
    indicatorState,
    isSending: isSendingExternal,
    send: handleMultiChannelSend,
    reengageOpen,
    setReengageOpen,
  } = useMultiChannelComposer({
    enabled: showMultiChannelComposer,
    hotelId: channelRelatedBooking?.hotelId ?? null,
    channel,
    messageComposer,
    textComposer,
    recordingActive: !!recordingController.recordingState,
    streamHandleSubmit: handleSubmit,
  });
  const checkoutStatus = channelRelatedBooking?.checkoutStatus;
  const preCheckoutStatus = channelRelatedBooking?.preCheckoutStatus;
  const hasSentPreCheckoutReminder =
    typeof preCheckoutStatus === "string" &&
    preCheckoutStatus.startsWith("SENT");

  const handleSaveNote = async (note: {
    content: string;
    plainText: string;
  }) => {
    if (!channel?.id) return;
    await createChatNote({ content: note.content });
  };

  const handleRequestSuccess = async (requestId: string) => {
    if (!channel) return;

    await channel.sendMessage({
      text: "",
      requestId,
    });
  };

  const handleSendPreCheckoutReminder = async () => {
    if (!bookingId) return;

    try {
      await sendPreCheckoutReminder(bookingId);
      toast.success(t("chats.preCheckoutReminderSent"));
    } catch (error) {
      toast.error(t(getPreCheckoutReminderErrorKey(error)));
    }
  };

  const insertComposerText = (value: string) => {
    const textarea = textareaRef.current;
    const currentText = textComposer.text ?? "";
    const start = textarea?.selectionStart ?? currentText.length;
    const end = textarea?.selectionEnd ?? currentText.length;

    textComposer.insertText({
      text: value,
      selection: { start, end },
    });

    setTimeout(() => {
      const nextTextarea = textareaRef.current;
      if (!nextTextarea) return;

      nextTextarea.focus();
      const newPosition = start + value.length;
      nextTextarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const replaceComposerText = (value: string) => {
    const textarea = textareaRef.current;
    const currentText = textComposer.text ?? "";

    messageComposer.textComposer.insertText({
      text: value,
      selection: {
        start: 0,
        end: currentText.length,
      },
    });

    if (textarea) {
      setTimeout(() => {
        textarea.focus();
        const cursorPosition = value.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
  };

  React.useEffect(() => {
    const handleComposerInsertText = (event: Event) => {
      const detail = (event as CustomEvent<ChatComposerInsertTextEventDetail>)
        .detail;
      if (!detail || typeof detail.text !== "string" || !detail.text.trim()) {
        return;
      }

      if (detail.mode === "replace") {
        replaceComposerText(detail.text);
        return;
      }

      insertComposerText(detail.text);
    };

    window.addEventListener(
      CHAT_COMPOSER_INSERT_TEXT_EVENT,
      handleComposerInsertText,
    );

    return () => {
      window.removeEventListener(
        CHAT_COMPOSER_INSERT_TEXT_EVENT,
        handleComposerInsertText,
      );
    };
  });

  const handleVariableSelect = (variable: string) => {
    insertComposerText(variable);
  };

  const handleEmojiSelect = (emoji: string) => {
    insertComposerText(emoji);
    setIsEmojiOpen(false);
  };

  const handleFormattedTextSave = (formattedText: string) => {
    const currentText = textComposer.text ?? "";
    const textarea = textareaRef.current;

    messageComposer.textComposer.insertText({
      text: formattedText,
      selection: {
        start: 0,
        end: currentText.length,
      },
    });

    if (textarea) {
      setTimeout(() => {
        textarea.focus();
        const cursorPosition = formattedText.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
  };

  const shouldSubmitComposerMessage = React.useCallback(
    // TextareaComposer should never run its default Stream submit here; this hook routes Enter through the multi-channel dispatcher.
    // eslint-disable-next-line sonarjs/no-invariant-returns
    (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      const shouldSubmit =
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing;

      if (!shouldSubmit) return false;

      event.preventDefault();
      if (!isSendingExternal) {
        void handleMultiChannelSend();
      }
      return false;
    },
    [handleMultiChannelSend, isSendingExternal],
  );

  if (recordingController.recordingState) return <AudioRecorder />;

  const recordingEnabled = !!(
    recordingController.recorder && navigator.mediaDevices
  );

  const text = textComposer.text;

  const actionButtonClass =
    "h-9 w-9 p-0 flex items-center justify-center bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700 transition-colors rounded-lg shrink-0 shadow-sm";
  const canUseChatAiControls =
    capabilities.canUseAiAssistant || capabilities.canUseGuestCommunication;
  const {
    shouldShowGuestRequestButton,
    shouldShowInternalRequestButton,
    shouldShowPreCheckoutReminderButton,
    shouldShowNoteButton,
    shouldShowActionBar,
    shouldShowFormatButton,
    shouldShowVariableButton,
    shouldShowTranslationSettings,
    shouldShowAiSettings,
    shouldShowSuggestButton,
  } = getMessageInputVisibility({
    canUseRequests: capabilities.canUseRequestsSystem,
    canUseAiAssistant: canUseChatAiControls,
    hideRequestButtons,
    hideVariableSelectors,
    hideAiSettingsPanel,
    hideSuggestButton,
    isInternalVariant,
    hasBooking: !!channelRelatedBooking,
    checkoutStatus,
  });
  const emojiOptions = [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "🙂",
    "🙃",
    "😉",
    "😊",
    "😇",
    "🥰",
    "😍",
    "🤩",
    "😘",
    "😗",
    "😋",
    "😛",
    "😜",
    "🤪",
    "😎",
    "🤓",
    "🧐",
    "🥳",
    "😌",
    "😏",
    "😴",
    "🤔",
    "🫡",
    "🤗",
    "🤭",
    "🫢",
    "🫣",
    "😶",
    "🫠",
    "😐",
    "😑",
    "😬",
    "🙄",
    "😮‍💨",
    "😮",
    "😯",
    "😲",
    "😳",
    "🥺",
    "😢",
    "😭",
    "😤",
    "😠",
    "😡",
    "🤯",
    "😱",
    "😨",
    "😰",
    "😥",
    "😓",
    "🤤",
    "😵",
    "🤐",
    "🤨",
    "🫤",
    "🤠",
    "😈",
    "👋",
    "🤚",
    "🖐️",
    "✋",
    "🫶",
    "👌",
    "🤌",
    "🤏",
    "✌️",
    "🤞",
    "🤟",
    "🤘",
    "🤙",
    "👈",
    "👉",
    "👆",
    "👇",
    "☝️",
    "👍",
    "👎",
    "👊",
    "✊",
    "🤛",
    "🤜",
    "👏",
    "🙌",
    "👐",
    "🤝",
    "🙏",
    "💪",
    "🫶",
    "💥",
    "💫",
    "💯",
    "💢",
    "💬",
    "🗨️",
    "🫰",
    "❤️",
    "🩷",
    "🧡",
    "💛",
    "💚",
    "🩵",
    "💙",
    "💜",
    "🤍",
    "🩶",
    "🖤",
    "🤎",
    "❤️‍🔥",
    "❤️‍🩹",
    "❣️",
    "💕",
    "💞",
    "💓",
    "💗",
    "💖",
    "💘",
    "💝",
    "🔥",
    "⭐",
    "🌟",
    "✨",
    "⚡",
    "☀️",
    "🌤️",
    "⛅",
    "🌈",
    "☁️",
    "❄️",
    "☔",
    "🌊",
    "🎉",
    "🎊",
    "🎈",
    "🎁",
    "🏆",
    "🥇",
    "🎯",
    "🎵",
    "🎶",
    "🎤",
    "📣",
    "📢",
    "✅",
    "❌",
    "❗",
    "❓",
    "⚠️",
    "🚀",
    "🛎️",
    "🏨",
    "🧹",
    "🧼",
    "🫧",
    "🧺",
    "🛏️",
    "🚪",
    "🪑",
    "🛁",
    "🚿",
    "🧻",
    "🧯",
    "🔑",
    "📍",
    "📌",
    "📞",
    "☎️",
    "📱",
    "💻",
    "🖥️",
    "⌚",
    "⏰",
    "🕒",
    "📅",
    "🗓️",
    "📝",
    "📋",
    "📦",
    "📬",
    "📤",
    "📥",
    "🧾",
    "💡",
    "🔦",
    "🔋",
    "🔌",
    "🍽️",
    "🍴",
    "☕",
    "🫖",
    "🥐",
    "🍞",
    "🥞",
    "🍳",
    "🥗",
    "🍲",
    "🍝",
    "🍕",
    "🍔",
    "🍟",
    "🌮",
    "🍣",
    "🍰",
    "🧁",
    "🍪",
    "🍫",
    "🍎",
    "🍋",
    "🍇",
    "🍓",
    "🥂",
    "🍷",
    "🍸",
    "🍹",
    "🥤",
    "🧃",
    "🚗",
    "🚕",
    "🚌",
    "✈️",
    "🧳",
    "🚨",
    "🚑",
    "👀",
    "🙈",
    "🙉",
    "🙊",
    "🐶",
    "🐱",
    "🦊",
    "🐻",
    "🐼",
    "🐵",
    "🌴",
    "🌷",
    "🌹",
    "🌺",
    "🌸",
    "🍀",
  ];

  return (
    <div className="bg-white p-4">
      {shouldShowActionBar ? (
        <RequestActionsBar
          bookingId={bookingId}
          roomId={roomId}
          shouldShowGuestRequestButton={shouldShowGuestRequestButton}
          shouldShowInternalRequestButton={shouldShowInternalRequestButton}
          shouldShowPreCheckoutReminderButton={
            shouldShowPreCheckoutReminderButton
          }
          shouldShowNoteButton={shouldShowNoteButton}
          guestRequestLabel={t("requests.page.guestRequest")}
          internalRequestLabel={t("requests.page.internalRequest")}
          preCheckoutReminderLabel={
            hasSentPreCheckoutReminder
              ? t("chats.resendPreCheckoutReminder")
              : t("chats.sendPreCheckoutReminder")
          }
          sendingPreCheckoutReminderLabel={t(
            "chats.sendingPreCheckoutReminder",
          )}
          addNoteLabel={t("chats.addNote")}
          onRequestSuccess={handleRequestSuccess}
          onSendPreCheckoutReminder={handleSendPreCheckoutReminder}
          onAddNote={() => setIsAddNoteOpen(true)}
          isSendingPreCheckoutReminder={isSendingPreCheckoutReminder}
          isAddNoteDisabled={!channel?.id}
        />
      ) : null}

      <div className="flex w-full flex-col rounded-xl border border-slate-200 bg-gray-100 shadow-sm transition-all focus-within:ring-1 focus-within:ring-slate-400">
        {!hideResolvePanel && (
          <div className="flex items-center justify-between border-b px-4 py-2 text-xs font-medium text-slate-500">
            <div className="flex grow items-center justify-between">
              <div />
              <div className="flex h-10 items-center gap-2">
                <FrontDeskUserSelect />
                <ResolveChatButton />
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            "bg-white px-4 py-2",
            hideResolvePanel && "rounded-t-xl",
          )}
        >
          <QuotedMessagePreview />
          <LinkPreviewList />
          <AttachmentPreviewList />

          <div className="relative min-h-[60px] text-sm">
            {!text && (
              <>
                {isInternalVariant ? (
                  <div className="pointer-events-none absolute inset-0 -top-1/2 z-10 flex items-center text-slate-400">
                    <span>Write your message here...</span>
                  </div>
                ) : (
                  <div className="pointer-events-none absolute inset-0 -top-1/2 z-10 flex items-center text-slate-400">
                    <span>Write your message here or use </span>
                    <button
                      type="button"
                      className="pointer-events-auto ml-1 hover:underline"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsCannedResponseOpen(true);
                      }}
                    >
                      pre-canned responses
                    </button>
                  </div>
                )}
              </>
            )}
            <TextareaComposer
              className={cn(
                "flex min-h-[60px] w-full bg-transparent px-0 py-2 pr-12 text-sm",
                "placeholder:text-slate-400",
                "focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                "resize-none border-0 ring-0 focus:ring-0 focus-visible:ring-0",
              )}
              containerClassName="flex min-h-[60px] w-full items-center"
              placeholder={" "}
              shouldSubmit={shouldSubmitComposerMessage}
            />
            {shouldShowSuggestButton ? <SuggestButton /> : null}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between border-t bg-gray-50/50 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className={actionButtonClass}>
              <SimpleAttachmentSelector />
            </div>

            <CannedResponsesToolbarButton
              className={actionButtonClass}
              isVisible={!isInternalVariant}
              label={t("chats.cannedResponses")}
              onOpen={() => setIsCannedResponseOpen(true)}
            />

            <Popover open={isEmojiOpen} onOpenChange={setIsEmojiOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(actionButtonClass, "border-slate-200")}
                >
                  <Smile className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                className="w-[420px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
              >
                <div className="mb-3 text-sm font-medium text-slate-600">
                  Choose emoji
                </div>
                <div className="grid max-h-[320px] grid-cols-9 gap-2 overflow-y-auto pr-1">
                  {emojiOptions.map((emoji, index) => (
                    <button
                      key={`${emoji}-${index}`}
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-[22px] transition-colors hover:bg-slate-100"
                      onClick={() => handleEmojiSelect(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {shouldShowVariableButton && (
              <>
                <div className={actionButtonClass}>
                  <VariableSelector onSelect={handleVariableSelect} />
                </div>
              </>
            )}

            {shouldShowFormatButton && (
              <>
                <div className={actionButtonClass}>
                  <button
                    type="button"
                    className="rounded p-2 px-3 font-serif text-lg transition-colors hover:bg-slate-200"
                    onClick={() => setIsFormatMessageOpen(true)}
                  >
                    T
                  </button>
                </div>
              </>
            )}

            {recordingEnabled && (
              <Button
                variant="outline"
                className={actionButtonClass}
                onClick={() => recordingController.recorder?.start()}
              >
                <Mic className="h-5 w-5" />
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <MultiChannelPickerSlot
              show={showMultiChannelComposer}
              selected={selectedHint}
              onSelect={setSelectedHint}
              configs={channelConfigs}
              unavailableChannels={unavailableChannels}
              disabled={isSendingExternal}
            />
            {shouldShowTranslationSettings && <TranslationSettingsSelect />}
            {shouldShowAiSettings ? <AIChatSettingsSelect /> : null}
            <Button
              onClick={handleMultiChannelSend}
              disabled={isSendingExternal}
              className="h-9 gap-2 rounded-lg bg-[#748bb2] px-4 text-white transition-all hover:bg-[#687da2] active:scale-95"
            >
              <span className="text-sm font-medium">{t("chats.send")}</span>
              <SendHorizontal className="h-4 w-4 text-white" />
            </Button>
          </div>
        </div>
        <MultiChannelIndicatorSlot
          show={showMultiChannelComposer}
          state={indicatorState}
        />
      </div>
      <AddNoteModal
        open={isAddNoteOpen}
        onOpenChange={setIsAddNoteOpen}
        onSave={handleSaveNote}
        isSaving={isCreatingNote}
      />
      <CannedResponseSelector
        onSelect={handleVariableSelect}
        open={isCannedResponseOpen}
        onOpenChange={setIsCannedResponseOpen}
        showTrigger={false}
      />
      <FormatMessageModal
        open={isFormatMessageOpen}
        onOpenChange={setIsFormatMessageOpen}
        initialValue={textComposer.text || ""}
        onSave={handleFormattedTextSave}
      />
      {channelRelatedBooking?.hotelId ? (
        <ReengagementDialog
          open={reengageOpen}
          onOpenChange={setReengageOpen}
          hotelId={channelRelatedBooking.hotelId}
          streamChannelId={channel?.id ?? null}
          templateContext={whatsappTemplateContext}
        />
      ) : null}
    </div>
  );
};

export default CustomMessageInput;
